import React from 'react';
import {
  Box,
  HStack,
  Text,
  VStack,
  Button,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Badge,
} from '@chakra-ui/react';
import { FaFilter, FaTimes } from 'react-icons/fa';

interface ClientNumberFilterProps {
  minClientNumber: number | null;
  maxClientNumber: number | null;
  onMinClientNumberChange: (value: number | null) => void;
  onMaxClientNumberChange: (value: number | null) => void;
  onClear: () => void;
  isActive: boolean;
}

export const ClientNumberFilter: React.FC<ClientNumberFilterProps> = ({
  minClientNumber,
  maxClientNumber,
  onMinClientNumberChange,
  onMaxClientNumberChange,
  onClear,
  isActive,
}) => {
  const formatClientNumber = (value: number) => {
    return value.toLocaleString('pt-BR');
  };

  const handleMinClientNumberChange = (valueAsString: string, valueAsNumber: number) => {
    if (valueAsString === '' || isNaN(valueAsNumber)) {
      onMinClientNumberChange(null);
    } else {
      onMinClientNumberChange(valueAsNumber);
    }
  };

  const handleMaxClientNumberChange = (valueAsString: string, valueAsNumber: number) => {
    if (valueAsString === '' || isNaN(valueAsNumber)) {
      onMaxClientNumberChange(null);
    } else {
      onMaxClientNumberChange(valueAsNumber);
    }
  };

  const getFilterText = () => {
    if (minClientNumber !== null && maxClientNumber !== null) {
      return `${formatClientNumber(minClientNumber)} - ${formatClientNumber(maxClientNumber)}`;
    } else if (minClientNumber !== null) {
      return `≥ ${formatClientNumber(minClientNumber)}`;
    } else if (maxClientNumber !== null) {
      return `≤ ${formatClientNumber(maxClientNumber)}`;
    }
    return 'Filtrar por cliente';
  };

  return (
    <Popover placement="bottom-start">
      <PopoverTrigger>
        <Button
          size="sm"
          variant={isActive ? 'solid' : 'outline'}
          colorScheme={isActive ? 'green' : 'gray'}
          leftIcon={<Icon as={FaFilter} />}
          rightIcon={isActive ? <Icon as={FaTimes} /> : undefined}
          onClick={isActive ? onClear : undefined}
          _hover={{
            transform: 'translateY(-1px)',
            boxShadow: 'md',
          }}
          transition="all 0.2s ease"
        >
          <HStack spacing={2}>
            <Text fontSize="xs" fontWeight="medium">
              {getFilterText()}
            </Text>
            {isActive && (
              <Badge colorScheme="green" variant="subtle" fontSize="xs">
                Ativo
              </Badge>
            )}
          </HStack>
        </Button>
      </PopoverTrigger>
      <PopoverContent width="400px" bg="white" border="1px solid" borderColor="gray.200" boxShadow="lg">
        <PopoverArrow />
        <PopoverBody p={4}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between" align="center">
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                Filtro por Número de Cliente
              </Text>
              {isActive && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={onClear}
                  leftIcon={<Icon as={FaTimes} />}
                >
                  Limpar
                </Button>
              )}
            </HStack>

            <HStack spacing={3} align="end">
              <VStack spacing={1} align="stretch" flex={1}>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">
                  Cliente Mínimo
                </Text>
                <NumberInput
                  value={minClientNumber || ''}
                  onChange={handleMinClientNumberChange}
                  min={0}
                  size="sm"
                >
                  <NumberInputField
                    placeholder="Ex: 1000"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: 'green.500',
                      boxShadow: '0 0 0 1px green.500',
                    }}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </VStack>

              <VStack spacing={1} align="stretch" flex={1}>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">
                  Cliente Máximo
                </Text>
                <NumberInput
                  value={maxClientNumber || ''}
                  onChange={handleMaxClientNumberChange}
                  min={minClientNumber || 0}
                  size="sm"
                >
                  <NumberInputField
                    placeholder="Ex: 9999"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: 'green.500',
                      boxShadow: '0 0 0 1px green.500',
                    }}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </VStack>
            </HStack>

            {isActive && (
              <Box fontSize="xs" color="green.600" bg="green.50" p={2} borderRadius="sm" border="1px solid" borderColor="green.200">
                <Text fontWeight="medium">
                  Filtro ativo: {minClientNumber ? formatClientNumber(minClientNumber) : 'Qualquer'} até{' '}
                  {maxClientNumber ? formatClientNumber(maxClientNumber) : 'Qualquer'}
                </Text>
              </Box>
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
